import { useEffect, useState } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Button, Select } from '../../../components/ui';
import SearchInput from '../../../components/ui/SearchInput';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import Spinner from '../../../components/ui/Spinner';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../components/icons';
import { useAdminTickets } from '../hooks/useAdminTickets';
import { AdminTicketDetailModal } from './AdminTicketDetailModal';
import { AdminTicketKpis } from './AdminTicketKpis';
import { AdminTicketsTable } from './AdminTicketsTable';
import { TICKET_STATUS_OPTIONS } from './ticketStatusLabels';

const DEBOUNCE_MS = 300;

export function AdminTicketsPage() {
    const {
        data,
        counts,
        detail,
        isLoading,
        isDetailLoading,
        isSaving,
        error,
        refetch,
        loadDetail,
        updateStatus,
        reply,
        page,
        setPage,
        setQ,
        status,
        setStatus,
        pageSize,
    } = useAdminTickets();

    const [searchInput, setSearchInput] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);

    useEffect(() => {
        const timer = setTimeout(() => {
            setQ(searchInput);
            setPage(1);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput, setQ, setPage]);

    const total = data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const canPrev = page > 1;
    const canNext = page < totalPages;
    const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const rangeEnd = Math.min(page * pageSize, total);

    const navBtn =
        'flex h-8 w-8 items-center justify-center rounded-md border border-subtle bg-card text-secondary transition-colors hover:bg-[color-mix(in_srgb,var(--color-surface-subtle)_50%,transparent)] disabled:cursor-not-allowed disabled:opacity-40';

    const handleSelect = (id: string) => {
        setSelectedId(id);
        void loadDetail(id);
    };

    const handleCloseModal = () => setSelectedId(null);

    const handleStatusFilter = (next: string) => {
        setStatus(next);
        setPage(1);
    };

    return (
        <div className="flex flex-col">
            <PageHeader
                title="Suporte"
                subtitle="Fila de suporte de todas as organizações"
                hideBackButton
                actionClassName="w-full min-w-0 md:w-auto"
                action={
                    <div className="w-full min-w-0 md:w-[280px]">
                        <SearchInput
                            value={searchInput}
                            onChange={setSearchInput}
                            placeholder="Buscar por assunto, org, e-mail..."
                        />
                    </div>
                }
            />

            <div className="mb-4 w-full max-w-xs">
                <Select
                    label="Status"
                    selectSize="sm"
                    value={status}
                    options={[{ value: '', label: 'Todos' }, ...TICKET_STATUS_OPTIONS]}
                    onChange={(e) => handleStatusFilter(e.target.value)}
                />
            </div>

            {isLoading && (
                <div className="flex items-center justify-center py-24">
                    <Spinner color="primary" size="lg" />
                </div>
            )}

            {!isLoading && error && (
                <Alert variant="destructive">
                    <AlertTitle>Erro ao carregar</AlertTitle>
                    <AlertDescription className="flex flex-col gap-3">
                        <p>{error}</p>
                        <Button variant="outline" size="sm" onClick={() => void refetch()}>
                            Tentar novamente
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            {!isLoading && !error && data && (
                <div className="min-w-0">
                    <AdminTicketKpis
                        counts={counts}
                        activeStatus={status}
                        onStatusClick={handleStatusFilter}
                    />
                    <AdminTicketsTable items={data.items} onSelect={handleSelect} />
                    {total > 0 && (
                        <div className="mt-3 flex flex-col gap-3 border-t border-subtle px-1 py-3 text-sm text-secondary sm:flex-row sm:items-center sm:justify-between sm:px-2">
                            <span>
                                {rangeStart}–{rangeEnd} de {total}
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-xs">
                                    Página {page} de {totalPages}
                                </span>
                                <button
                                    type="button"
                                    className={navBtn}
                                    onClick={() => setPage(page - 1)}
                                    disabled={!canPrev}
                                    aria-label="Página anterior"
                                >
                                    <ChevronLeftIcon className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    className={navBtn}
                                    onClick={() => setPage(page + 1)}
                                    disabled={!canNext}
                                    aria-label="Próxima página"
                                >
                                    <ChevronRightIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <AdminTicketDetailModal
                ticketId={selectedId}
                detail={detail}
                isOpen={selectedId !== null}
                isLoading={isDetailLoading}
                isSaving={isSaving}
                onClose={handleCloseModal}
                onReply={reply}
                onUpdateStatus={updateStatus}
            />
        </div>
    );
}
